"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, CornerDownLeft, Download, FolderDown, ImageIcon, ImagePlus, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import prettyBytes from "pretty-bytes";
import { memo, useEffect, useState } from "react";
import { toast } from "sonner";
import { Actions, Action } from "@/components/ai-elements/actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageZoom } from "@/components/ui/image-zoom";
import { Skeleton } from "@/components/ui/skeleton";
import { useGalleryImage } from "@/hooks/useGalleryImage";
import { heroSuggestionGroups, sampleSuggestions } from "@/lib/hero-suggestions";
import { downloadImage, downloadImagesAsZip, imageFileStem, type ZipEntry } from "@/lib/image-download";
import type { GalleryImage, GalleryItem } from "@/lib/image-gen";
interface ImageGalleryProps {
  items: GalleryItem[];
  comparisonMode: boolean;
  onSuggestionClick?: (prompt: string) => void;
  onUseAsReference?: (image: GalleryImage) => void;
  onInsertPrompt?: (prompt: string) => void;
}
function describeError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}
function modelSlug(modelId: string) {
  return modelId.replace(/[^a-z0-9.-]+/gi, "-");
}
const GalleryImageTile = memo(({
  image,
  stem,
  modelName
}: {
  image: GalleryImage;
  stem: string;
  modelName?: string;
}) => {
  const resolved = useGalleryImage(image);
  const [isDownloading, setIsDownloading] = useState(false);
  const details = resolved?.width && resolved.height ? `${resolved.width}×${resolved.height}${resolved.bytes ? ` · ${prettyBytes(resolved.bytes)}` : ""}` : null;
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadImage(image, stem);
    } catch (error) {
      toast.error(describeError(error, "Download failed"));
    } finally {
      setIsDownloading(false);
    }
  };
  return <div className="group relative h-64 overflow-hidden rounded-lg border bg-muted/30">
				{resolved ? <ImageZoom className="h-full w-full" zoomImg={{
      src: resolved.fullUrl,
      alt: "Generated image"
    }}>
						<img src={resolved.previewUrl} alt="Generated image" loading="lazy" decoding="async" className="h-full w-full object-cover" />
					</ImageZoom> : <Skeleton className="h-full w-full rounded-none" />}
				<div className="absolute top-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
					<Button variant="secondary" size="icon" aria-label="Download image" className="h-8 w-8 bg-background/80 backdrop-blur-sm" disabled={!resolved || isDownloading} onClick={handleDownload}>
						{isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
					</Button>
				</div>
				{(modelName || details) && <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
						{modelName && <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-xs">
								{modelName}
							</Badge>}
						{details && <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm text-xs tabular-nums">
								{details}
							</Badge>}
					</div>}
			</div>;
});
GalleryImageTile.displayName = "GalleryImageTile";
function LoadingSkeleton(t0) {
  const $ = _c(2);
  const {
    count
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = <div className="grid grid-cols-2 gap-3 max-w-lg">{Array.from({
        length: count
      }).map(_temp)}</div>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp(_, i) {
  return <Skeleton key={i} className="h-64 rounded-lg" />;
}
function EmptyState(t0) {
  const $ = _c(10);
  const {
    onSuggestionClick
  } = t0;
  const [suggestions, setSuggestions] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSuggestions(sampleSuggestions(heroSuggestionGroups["Image gen"], 6));
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ImageIcon className="h-16 w-16 text-muted-foreground/30 mb-6" />;
    t4 = <h3 className="text-lg font-medium mb-2">No images yet</h3>;
    t5 = <p className="text-sm text-muted-foreground mb-8 max-w-md">Describe what you want to create and click Generate to get started.</p>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onSuggestionClick || $[6] !== suggestions) {
    t6 = suggestions ? <motion.div key="suggestions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.07,
      ease: "easeOut"
    }} className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">{suggestions.map((s, index) => <motion.button key={s} type="button" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.12,
        delay: index * 0.025,
        ease: "easeOut"
      }} onClick={() => onSuggestionClick?.(s)} className="rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60 transition-colors">{s}</motion.button>)}</motion.div> : null;
    $[5] = onSuggestionClick;
    $[6] = suggestions;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex flex-col items-center justify-center py-20 text-center">{t3}{t4}{t5}<AnimatePresence>{t6}</AnimatePresence></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function InputImageThumbnail(t0) {
  const $ = _c(4);
  const {
    image,
    index
  } = t0;
  const resolved = useGalleryImage(image);
  if (!resolved) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Skeleton className="h-6 w-6 rounded" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = `Input ${index + 1}`;
  let t2;
  if ($[1] !== resolved.previewUrl || $[2] !== t1) {
    t2 = <img src={resolved.previewUrl} alt={t1} loading="lazy" className="h-6 w-6 rounded border object-cover" />;
    $[1] = resolved.previewUrl;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function InputImageThumbnails(t0) {
  const $ = _c(4);
  const {
    images
  } = t0;
  if (!images || images.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== images) {
    t1 = images.map(_temp2);
    $[0] = images;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex gap-1 shrink-0">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(image, i) {
  return <InputImageThumbnail key={i} image={image} index={i} />;
}
function ItemHeader(t0) {
  const $ = _c(12);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.inputImages) {
    t1 = <InputImageThumbnails images={item.inputImages} />;
    $[0] = item.inputImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.prompt) {
    t2 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[2] = item.prompt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.timestamp) {
    t3 = new Date(item.timestamp).toLocaleTimeString();
    $[4] = item.timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-xs text-muted-foreground shrink-0">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ModelError(t0) {
  const $ = _c(3);
  const {
    message
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertCircle className="h-4 w-4 text-destructive shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== message) {
    t2 = <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5">{t1}<p className="text-sm text-destructive">{message}</p></div>;
    $[1] = message;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Per-tile download names: "<prompt>-2.png" in single mode, and
// "<prompt>-<model>-2.png" when several models are compared.
function tileStem(item: GalleryItem, modelId: string, index: number) {
  const stem = imageFileStem(item.prompt);
  const suffix = item.models.length > 1 ? `-${modelSlug(modelId)}` : "";
  const images = item.models.find(m => m.modelId === modelId)?.images ?? [];
  return images.length > 1 || suffix ? `${stem}${suffix}-${index + 1}` : stem;
}
function ItemActions({
  item,
  onUseAsReference,
  onInsertPrompt
}: {
  item: GalleryItem;
  onUseAsReference?: (image: GalleryImage) => void;
  onInsertPrompt?: (prompt: string) => void;
}) {
  const [isZipping, setIsZipping] = useState(false);
  const firstImage = item.models[0]?.images[0];
  const entries: ZipEntry[] = item.models.flatMap(model => model.images.map((image, index) => ({
    image,
    stem: tileStem(item, model.modelId, index)
  })));
  if (!firstImage && !onInsertPrompt) {
    return null;
  }
  const handleDownloadAll = async () => {
    setIsZipping(true);
    try {
      await downloadImagesAsZip(entries, imageFileStem(item.prompt));
    } catch (error) {
      toast.error(describeError(error, "Download failed"));
    } finally {
      setIsZipping(false);
    }
  };
  return <Actions>
			{entries.length > 1 && <Action tooltip={`Download all ${entries.length} images as a zip`} disabled={isZipping} onClick={handleDownloadAll}>
					{isZipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderDown className="h-4 w-4" />}
				</Action>}
			{firstImage && onUseAsReference && <Action tooltip="Use as image reference" onClick={() => onUseAsReference(firstImage)}>
					<ImagePlus className="h-4 w-4" />
				</Action>}
			{onInsertPrompt && <Action tooltip="Insert prompt" onClick={() => onInsertPrompt(item.prompt)}>
					<CornerDownLeft className="h-4 w-4" />
				</Action>}
		</Actions>;
}
function SingleModeItem(t0) {
  const $ = _c(18);
  const {
    item,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  const model = item.models[0];
  if (!model) {
    return null;
  }
  let t1;
  if ($[0] !== item) {
    t1 = <ItemHeader item={item} />;
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item || $[3] !== model.error || $[4] !== model.imageCount || $[5] !== model.images || $[6] !== model.isLoading || $[7] !== model.modelId) {
    t2 = model.error ? <ModelError message={model.error} /> : model.images.length === 0 && model.isLoading ? <LoadingSkeleton count={Math.max(1, model.imageCount ?? 1)} /> : <div className={`grid gap-3 ${model.images.length === 1 && !model.isLoading ? "grid-cols-1 max-w-xs" : "grid-cols-2 max-w-lg"}`}>{model.images.map((image, idx) => <GalleryImageTile key={idx} image={image} stem={tileStem(item, model.modelId, idx)} />)}{model.isLoading && <Skeleton className="h-64 rounded-lg" />}</div>;
    $[2] = item;
    $[3] = model.error;
    $[4] = model.imageCount;
    $[5] = model.images;
    $[6] = model.isLoading;
    $[7] = model.modelId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== item || $[10] !== model.isLoading || $[11] !== onInsertPrompt || $[12] !== onUseAsReference) {
    t3 = !model.isLoading && <ItemActions item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} />;
    $[9] = item;
    $[10] = model.isLoading;
    $[11] = onInsertPrompt;
    $[12] = onUseAsReference;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = <div className="space-y-2">{t1}{t2}{t3}</div>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
function ComparisonModeItem(t0) {
  const $ = _c(15);
  const {
    item,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = <ItemHeader item={item} />;
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `grid gap-4 ${item.models.length === 1 ? "grid-cols-1" : item.models.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`;
  let t3;
  if ($[2] !== item) {
    t3 = item.models.map(model => <div key={model.modelId} className="space-y-2"><Badge variant="outline" className="text-xs">{model.modelName}</Badge>{model.error ? <ModelError message={model.error} /> : model.images.length === 0 && model.isLoading ? <LoadingSkeleton count={Math.max(1, model.imageCount ?? 1)} /> : <div className={`grid gap-2 ${model.images.length === 1 && !model.isLoading ? "grid-cols-1" : "grid-cols-2"}`}>{model.images.map((image, idx) => <GalleryImageTile key={idx} image={image} stem={tileStem(item, model.modelId, idx)} modelName={model.modelName} />)}{model.isLoading && <Skeleton className="h-64 rounded-lg" />}</div>}</div>);
    $[2] = item;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item || $[8] !== onInsertPrompt || $[9] !== onUseAsReference) {
    t5 = !item.models.some(_temp3) && <ItemActions item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} />;
    $[7] = item;
    $[8] = onInsertPrompt;
    $[9] = onUseAsReference;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="space-y-2">{t1}{t4}{t5}</div>;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp3(m) {
  return m.isLoading;
}
export function ImageGallery(t0) {
  const $ = _c(13);
  const {
    items,
    comparisonMode,
    onSuggestionClick,
    onUseAsReference,
    onInsertPrompt
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== onSuggestionClick) {
      t1 = <EmptyState onSuggestionClick={onSuggestionClick} />;
      $[0] = onSuggestionClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== comparisonMode || $[3] !== items || $[4] !== onInsertPrompt || $[5] !== onUseAsReference) {
    let t2;
    if ($[7] !== comparisonMode || $[8] !== onInsertPrompt || $[9] !== onUseAsReference) {
      t2 = item => <div key={item.id} id={`gallery-${item.id}`}>{comparisonMode ? <ComparisonModeItem item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} /> : <SingleModeItem item={item} onUseAsReference={onUseAsReference} onInsertPrompt={onInsertPrompt} />}</div>;
      $[7] = comparisonMode;
      $[8] = onInsertPrompt;
      $[9] = onUseAsReference;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = items.map(t2);
    $[2] = comparisonMode;
    $[3] = items;
    $[4] = onInsertPrompt;
    $[5] = onUseAsReference;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <div className="space-y-8">{t1}</div>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}